import { PAYMENT_ERROR_CODE } from "@/config";
import type { SubmissionSuccessResponse } from "@/types/Payment";
import type { ServerErrorResponse } from "@/types/ServerError";
import { ApiError } from "@/utils/api";

// Errors raised by fetch itself (network drop), by withTimeout, or by an
// aborted request: the POST may well have reached the backend.
const NO_RESPONSE_ERROR_NAMES = new Set([
  "TypeError",
  "TimeoutError",
  "AbortError",
]);

export function serverErrorBody(error: unknown): ServerErrorResponse {
  const body = error instanceof ApiError ? error.body : undefined;
  return (body ?? error) as ServerErrorResponse;
}

export function isPaymentProcessingError(error: unknown): boolean {
  return serverErrorBody(error)?.error_type === PAYMENT_ERROR_CODE;
}

/**
 * The backend has ruled on this session and did not charge: an unusable
 * session (failed / expired / not_found / form_mismatch / amount_mismatch),
 * a gateway decline (502, or the 422 the gateway's validation decline
 * surfaces as), or a blocked payment. Replacing the session is then
 * safe — and necessary, or a declined card could never be swapped for
 * another. `charged_pending_review` is the one unusable reason that did
 * charge; it is checked before `failed` server-side, so a `failed` verdict
 * never hides a charge.
 */
export function isSessionVerdictError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  const body = error.body as Partial<ServerErrorResponse> | undefined;
  if (body?.error === "session_unusable") {
    return body.reason !== "charged_pending_review";
  }
  return (
    body?.error === "payment_declined" ||
    body?.error_type === PAYMENT_ERROR_CODE ||
    isExpectedPaymentValidationError(error)
  );
}

/**
 * The donor asked for a new session while the one being replaced had already
 * been charged and stored. Nothing is wrong: their donation went through, and
 * the backend hands back the stored success payload so the form can show the
 * thank-you page instead of asking them to pay again.
 */
export function completedDonationFromReplacedSession(
  error: unknown,
): SubmissionSuccessResponse | null {
  if (!(error instanceof ApiError) || error.status !== 409) {
    return null;
  }

  const body = error.body as
    | { error?: string; submission_response?: SubmissionSuccessResponse }
    | undefined;

  if (body?.error !== "session_replaced_confirmed") {
    return null;
  }

  return body.submission_response ?? null;
}

/**
 * The backend refused to replace a session that may have taken the donor's
 * money. `session_replaced_confirmed` is the settled case handled above;
 * these two leave the donor on the form, so the caller must turn them into a
 * banner rather than an unhandled rejection.
 */
export function replacedSessionRefusal(
  error: unknown,
): "charged_pending_review" | "confirm_in_progress" | null {
  if (!(error instanceof ApiError) || error.status !== 409) {
    return null;
  }

  const body = error.body as Partial<ServerErrorResponse> | undefined;

  if (body?.error === "session_replaced_charged_pending_review") {
    return "charged_pending_review";
  }

  if (body?.error === "session_replaced_confirm_in_progress") {
    return "confirm_in_progress";
  }

  return null;
}

/** The gateway charged but storing the donation failed: nothing the donor
 * can do will finish it, so the form must not offer a retry. */
export function isChargedPendingReviewError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 410 &&
    (error.body as Partial<ServerErrorResponse> | undefined)?.reason ===
      "charged_pending_review"
  );
}

/**
 * True when the backend may have charged the donor but the browser never
 * learned the outcome. The payment session must then be kept: retrying on it
 * is safe (the backend replays a stored result instead of charging again),
 * whereas replacing it turns the retry into a second charge. A false positive
 * costs the banner copy and skips one session replacement (the retry still
 * works on the kept session), so the classifier errs towards "unknown".
 */
export function isUnknownOutcomeError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return error instanceof Error && NO_RESPONSE_ERROR_NAMES.has(error.name);
  }

  const body = error.body as Partial<ServerErrorResponse> | undefined;

  if (error.status === 409 && body?.error === "confirm_in_progress") {
    return true;
  }
  if (isChargedPendingReviewError(error)) {
    return true;
  }

  // A 5xx from the proxy (502/504 with an HTML or empty body) hides whether
  // PHP ran; the backend's own decline is the one 5xx that is definitive.
  return error.status >= 500 && body?.error !== "payment_declined";
}

export function isExpectedPaymentValidationError(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 422) {
    return false;
  }

  const errors = serverErrorBody(error)?.errors;

  return (
    errors !== null &&
    typeof errors === "object" &&
    Object.keys(errors).length === 1 &&
    Array.isArray(errors.payment) &&
    errors.payment.length > 0
  );
}

export function isExpiredPaymentSession(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 410) return false;
  const body = error.body as { error?: string; reason?: string } | null;
  return body?.error === "session_unusable" && body.reason === "expired";
}

// ConfirmPaymentRequest keys bank detail errors under `fields.bank_details.*`;
// the form's processors read them as `bank_details.*`.
const BANK_DETAILS_ERROR_KEY = /^(fields\.)?bank_details\./;

function isBankDetailsRejection(error: ApiError): boolean {
  if (error.status !== 422) return false;

  const errors = serverErrorBody(error)?.errors;

  if (errors === null || typeof errors !== "object") return false;

  const keys = Object.keys(errors);

  return (
    keys.length > 0 && keys.every((key) => BANK_DETAILS_ERROR_KEY.test(key))
  );
}

/**
 * The backend refused the submission for a reason the donor can fix or wait
 * out: their session expired, they hit the rate limit, or their bank details
 * failed validation. Worth counting in Sentry, but not a defect.
 */
export function isExpectedSubmissionRejection(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;

  return (
    isExpiredPaymentSession(error) ||
    error.status === 429 ||
    isBankDetailsRejection(error)
  );
}
